<script lang="ts">
  import { VList } from 'virtua/svelte'

  import ChevronRight from '@lucide/svelte/icons/chevron-right'
  import ChevronLeft from '@lucide/svelte/icons/chevron-left'

  let {
    serverid,
    tags,
    header,
    kind = 'tag',
  }: {
    serverid: string
    tags: string[]
    header: string
    kind?: 'genre' | 'tag'
  } = $props()

  let maxWidth = $state(0)
  let itemWidth = $state(279.5)
  let columns = $state(3)

  let list: VList<any>

  let thumbRatio = $state(1)
  let thumbOffset = $state(0)
  const showThumb = $derived(thumbRatio < 0.999)

  let hLim = $derived(maxWidth < 370 ? 170 : 280)

  /** Match `h-30` cards; vertical pad so hover:scale-[1.03] does not clip. */
  const cardHeightRem = 7.5 // h-30
  const hoverPadRem = 0.5
  const listHeightRem = cardHeightRem + hoverPadRem * 2

  const accentWash =
    kind === 'genre'
      ? 'bg-linear-to-br from-latte-mauve/15 to-transparent dark:from-macchiato-mauve/20'
      : 'bg-linear-to-br from-latte-sky/15 to-transparent dark:from-macchiato-sky/20'

  const cardHover =
    kind === 'genre'
      ? 'hover:border-latte-mauve/45 dark:hover:border-macchiato-mauve/45'
      : 'hover:border-latte-sky/45 dark:hover:border-macchiato-sky/45'

  function syncThumb() {
    if (!list) return
    const scrollLeft = list.getScrollOffset()
    const scrollWidth = list.getScrollSize()
    const clientWidth = list.getViewportSize()
    if (scrollWidth <= 0 || clientWidth <= 0) return
    thumbRatio = Math.min(1, clientWidth / scrollWidth)
    const maxScroll = scrollWidth - clientWidth
    thumbOffset =
      maxScroll > 0 ? (scrollLeft / maxScroll) * (1 - thumbRatio) * 100 : 0
  }

  function scrollByItems(dir: -1 | 1) {
    if (!list || !tags?.length) return
    const current = list.findItemIndex(list.getScrollOffset())
    const next = Math.max(0, Math.min(tags.length - 1, current + dir * 2))
    list.scrollToIndex(next, { align: 'start', smooth: true })
  }

  $effect(() => {
    if (!maxWidth) return

    for (let i = 9; i > 1; i--) {
      const h = ((maxWidth - 10) * 1.492) / i
      if (h > hLim) {
        // Grid cell width from layout px; height chrome is rem (listHeightRem).
        itemWidth = (maxWidth - 10) / i
        columns = i
        break
      }
    }
  })

  $effect(() => {
    void tags
    void itemWidth
    requestAnimationFrame(syncThumb)
  })
</script>


<div class="w-full h-0" bind:clientWidth={null, v => (maxWidth = v!)}></div>

<div class="mt-20 flex items-stretch px-4 sm:px-10">
  <h1 class="text-xl font-bold text-latte-text dark:text-macchiato-text">{header}</h1>
  <span class="grow"></span>
  <button
    type="button"
    class="text-latte-surface1 transition-colors hover:text-latte-mauve dark:text-macchiato-surface1 dark:hover:text-macchiato-mauve"
    onclick={() => scrollByItems(-1)}
  >
    <ChevronLeft class="size-6 shrink-0" />
  </button>
  <span class="w-6"></span>
  <button
    type="button"
    class="text-latte-surface1 transition-colors hover:text-latte-mauve dark:text-macchiato-surface1 dark:hover:text-macchiato-mauve"
    onclick={() => scrollByItems(1)}
  >
    <ChevronRight class="size-6 shrink-0" />
  </button>
</div>
{#key tags}
  <div class="relative pb-2.5">
    <VList
      data={tags}
      bind:this={list}
      style={`height: ${listHeightRem}rem;`}
      class="scrollbar-none px-5"
      horizontal
      getKey={(_, i) => i}
      onscroll={syncThumb}
    >
      {#snippet children(item, index)}
        <div
          class="mx-4 flex shrink-0 items-center"
          style="height: {listHeightRem}rem; width: {itemWidth}px;"
        >
          <div
            class="relative grid h-30 w-full origin-center items-center overflow-hidden rounded-xl border border-latte-surface1/60 bg-latte-mantle px-4 shadow-sm transition-transform hover:scale-[1.03] hover:opacity-95 dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle {cardHover}"
          >
            <div class="pointer-events-none absolute inset-0 {accentWash}" aria-hidden="true"></div>
            <div class="relative mx-auto text-center text-3xl font-bold text-latte-text dark:text-macchiato-text">
              {item}
            </div>
          </div>
        </div>
      {/snippet}
    </VList>
    {#if showThumb}
      <div class="pointer-events-none absolute inset-x-5 bottom-3 h-1.5">
        <div
          class="absolute top-0 h-full rounded-full bg-gray-600/70"
          style="width: {thumbRatio * 100}%; left: {thumbOffset}%"
        ></div>
      </div>
    {/if}
  </div>
{/key}
